import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import MediaQuery from 'react-responsive';
import { useHistory, useLocation } from 'react-router';

import {
  getRecentSearchedProducts,
  getUniversalSearchProds,
  saveProductSearches,
} from 'actions/trade';
import { isAuthenticated } from 'actions/user';
import { TAB_CHANGE } from 'actionTypes/appDrawer';
import SearchBox from 'components/Header/partials/headerDropdown/SearchBox';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { device } from 'helpers/screenSizes';
import { getProductUrlByContractType } from 'helpers/utils';
// eslint-disable-next-line local-rules/no-direct-import
import { useAppSelector, useFavorite, useLockedBody } from 'hooks';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { userSelector } from 'selectors/otherSelectors';
import { sortProductsByVolume } from 'selectors/tickerSelectors';
import { Product } from 'types/IProducts';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton';

import { FavoritesContext, getMixpanelPageName } from './partials/helpers';
import { SearchData, UniversalSearchResponse } from './partials/interface';
import SearchResultsSection from './partials/searchResultsSection';
import styles from './universal-search.module.scss';

const UniversalSearch = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useAppSelector(userSelector);
  const isLoggedin = isAuthenticated(user);
  const [recentSearches, setRecentSearches] = useState<any[]>([]);

  const { t } = useTranslation(['headerFooter', 'trading', 'common']);

  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [searchParam, setSearchParam] = useState<string>('');
  const [searchData, setSearchData] = useState<SearchData>({});
  const [noResultsFound, setNoResultsFound] = useState<boolean>(false);

  const { favoritesIds, toggleFavorite } = useFavorite();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceCallback = useCallback(
    debounce(value => {
      if (value.length) {
        dispatch(getUniversalSearchProds(value)).then(
          (response: UniversalSearchResponse) => {
            if (response.success) {
              const {
                futures,
                spot,
                spreads,
                interest_rate_swaps: interestRateSwaps,
                move_options: moveOptions,
                call_options: callOptions,
                put_options: putOptions,
                perpetual_futures: perpetualFutures,
                options_combos: optionsCombos,
                tracker,
              } = response.result;

              const futuresData = [...futures.products, ...perpetualFutures.products];
              const optionsData = [...putOptions.products, ...callOptions.products].slice(
                0,
                5
              );

              const data = {
                futures: {
                  count: futures.count + perpetualFutures.count,
                  data: futuresData.sort(sortProductsByVolume('asc')),
                },
                options: {
                  count: putOptions.count + callOptions.count,
                  data: optionsData.sort(sortProductsByVolume('asc')),
                },
                spot: {
                  count: spot.count,
                  data: spot.products.sort(sortProductsByVolume('asc')),
                },
                move: {
                  count: moveOptions.count,
                  data: moveOptions.products.sort(sortProductsByVolume('asc')),
                },
                optionsCombos: {
                  count: optionsCombos.count,
                  data: optionsCombos.products.sort(sortProductsByVolume('asc')),
                },
                spreads: {
                  count: spreads.count,
                  data: spreads.products.sort(sortProductsByVolume('asc')),
                },
                irs: {
                  count: interestRateSwaps.count,
                  data: interestRateSwaps.products.sort(sortProductsByVolume('asc')),
                },
                tracker: {
                  count: tracker.count,
                  data: tracker.products.sort(sortProductsByVolume('asc')),
                },
              };

              const dataLength =
                data.futures.count +
                data.spot.count +
                data.options.count +
                data.optionsCombos.count +
                data.move.count +
                data.spreads.count +
                data.irs.count +
                data.tracker.count;

              setSearchData(data);
              setDropdownOpen(true);
              setNoResultsFound(dataLength === 0);
            }
          }
        );
      }
    }, 200),
    []
  );

  const onSearchChange = (value: string) => {
    const filteredValue = value.replace(/(<|>|\(|\)|&|\||:|;|'|\*|{|}|")/g, '');
    setSearchParam(filteredValue);
    if (filteredValue.length <= 1) {
      setDropdownOpen(true);
      setNoResultsFound(false);
      setSearchData({});
    } else {
      debounceCallback(filteredValue);
    }
  };

  const clearSearch = () => {
    setSearchParam('');
    setSearchData({});
    setNoResultsFound(false);
  };

  const onOptionsBlur = (e: React.MouseEvent<Document>) => {
    if (e.relatedTarget === null) {
      clearSearch();
      setDropdownOpen(false);
    }
  };

  const onFocus = () => {
    setDropdownOpen(true);
    trackMixpanelEvent('Search- Initiated', {
      source: getMixpanelPageName(),
    });
  };

  const trackSearchResultClicked = (product: Product) => {
    trackMixpanelEvent('Search- Search Result Clicked', {
      source: getMixpanelPageName(),
      search_term: searchParam,
      contract_name: product.symbol,
    });
  };
  const getRecentSearches = useCallback(async () => {
    try {
      const data = await dispatch(getRecentSearchedProducts());
      setRecentSearches(data.result);
    } catch (err) {
      console.error('err debug', err);
    }
  }, [dispatch]);

  const saveProductSearch = product => {
    const payload = {
      product_symbol: product.symbol,
    };
    dispatch(saveProductSearches(payload)).then(() => {
      getRecentSearches();
    });
  };

  const handleCloseSearchDropdown = () => {
    setDropdownOpen(false);
  };
  const onRowClick = (product: Product, isRecentlySearched: boolean = false) => {
    if (!noResultsFound && Object.keys(searchData).length) {
      trackSearchResultClicked(product);
    }
    if (!isRecentlySearched && isLoggedin) {
      saveProductSearch(product);
    }
    const contractType = product?.contract_type;
    const underlyingAsset = product?.underlying_asset;
    const symbol = product?.symbol;
    const url = getProductUrlByContractType(
      contractType,
      underlyingAsset?.symbol,
      symbol
    );
    clearSearch();
    handleCloseSearchDropdown();
    dispatch({
      type: TAB_CHANGE,
      payload: APP_DRAWER_TAB_ID_MAPPING.TRADE,
    });
    history.push(url);
  };

  useEffect(() => {
    if (isLoggedin) {
      getRecentSearches();
      return;
    }

    setRecentSearches([]);
  }, [isLoggedin, getRecentSearches]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // focus on universal-search input element on backslash('/') click
      // if (e.key === '/') {
      //   e.preventDefault();
      //   if (inputRef.current) {
      //     inputRef.current.focus();
      //   }
      // }
      // on escape keypress close the popup and clear search
      if (e.key === 'Escape') {
        e.preventDefault();
        clearSearch();
        setDropdownOpen(false);
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    const unlisten = history.listen(() => {
      handleCloseSearchDropdown();
    });
    return () => {
      unlisten();
    };
  }, [history]);

  useLockedBody(isDropdownOpen);

  const location = useLocation();

  const onBackClick = () => {
    if (location.state?.prevPath) {
      history.push({
        pathname: location.state.prevPath,
        state: { prevPath: location.pathname },
      });
    } else {
      history.push(routes.webAppHome);
    }
  };

  const favoritesContextValue = useMemo(() => {
    return {
      favoritesIds,
      toggleFavorite,
    };
  }, [favoritesIds, toggleFavorite]);

  return (
    (<FavoritesContext.Provider value={favoritesContextValue} data-palette="UniversalSearch">
      <MediaQuery query={device.up.md}>
        <div className={`${styles['header-dropdown-wrapper']}`}>
          <div className={styles.headerDropdownWrapper}>
            <ClickAwayListener onClickAway={onOptionsBlur}>
              <div className={styles.universalSearch}>
                <SearchBox
                  onSearchChange={onSearchChange}
                  searchValue={searchParam}
                  clearSearch={clearSearch}
                  ref={inputRef}
                  placeholder={t('headerFooter:header.search')}
                  headerInputStyles={styles.headerInput}
                  id="universal-search"
                  showBackSlash={false}
                  crossIconClassName="circle-icon"
                  onFocus={onFocus}
                  autoFocus={false}
                  className={styles.border}
                />
                {isDropdownOpen && (
                  <div
                    className={`${styles['dropdown-backdrop-wrapper']} ${styles['header-dropdown-container']}`}
                    data-palette="UniversalSearch">
                    <SearchResultsSection
                      searchData={searchData}
                      clearSearch={clearSearch}
                      onRowClick={onRowClick}
                      noResultsFound={noResultsFound}
                      searchTerm={searchParam}
                      recentSearches={recentSearches}
                      onClose={handleCloseSearchDropdown}
                    />
                    <Box
                      as="div"
                      data-testid="universal-search-backdrop"
                      className={styles.backdrop}
                      onClick={onOptionsBlur}
                    />
                  </div>
                )}
              </div>
            </ClickAwayListener>
          </div>
        </div>
      </MediaQuery>
      <MediaQuery query={device.down.md}>
        <div className={styles.mobileWrapper}>
          <div className={styles.searchSection}>
            <SearchBox
              onSearchChange={onSearchChange}
              searchValue={searchParam}
              clearSearch={clearSearch}
              ref={inputRef}
              placeholder={t('headerFooter:header.search')}
              headerInputStyles={styles.headerInput}
              id="universal-search-mobile"
              crossIconClassName="circle-icon"
              onFocus={onFocus}
              autoFocus
              className={styles.border}
            />
            <DeltaButton
              useBasicButton
              data-testid="universal-search-mobile-cancelBtn"
              testId="universal-search-mobile-cancelBtn"
              onClick={onBackClick}
              className={styles.cancelBtn}
            >
              {t('common:cancel')}
            </DeltaButton>
          </div>

          <div>
            <SearchResultsSection
              searchData={searchData}
              clearSearch={clearSearch}
              onRowClick={onRowClick}
              noResultsFound={noResultsFound}
              searchTerm={searchParam}
              recentSearches={recentSearches}
            />
          </div>
        </div>
      </MediaQuery>
    </FavoritesContext.Provider>)
  );
};

export default UniversalSearch;
